import React, { useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import type { SxProps, Theme } from '@mui/material/styles';

const DURATION = 380;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

interface FloatingContainerMorphProps extends React.HTMLAttributes<HTMLDivElement> {
  minimized: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
  onBack?: () => void;
  icon: React.ReactNode;
  title: string;
  onTitleClick?: () => void;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const FloatingContainerMorph: React.FC<FloatingContainerMorphProps> = ({
  minimized,
  onMinimize,
  onExpand,
  onBack,
  icon,
  title,
  onTitleClick,
  children,
  sx,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (minimized) {
      const startW = container.offsetWidth;
      const targetW = (titleGroupRef.current?.scrollWidth ?? 0) + 34;
      // Pin current width, force reflow, then animate to target
      container.style.width = `${startW}px`;
      container.style.transition = 'none';
      void container.offsetWidth; // force reflow
      container.style.transition = `width ${DURATION}ms ${EASE}`;
      container.style.width = `${targetW}px`;
    } else {
      // Animate from current (narrow) width back to natural width
      const currentW = container.offsetWidth;
      container.style.transition = 'none';
      container.style.width = '';
      const naturalW = container.offsetWidth;
      container.style.width = `${currentW}px`;
      void container.offsetWidth; // force reflow
      container.style.transition = `width ${DURATION}ms ${EASE}`;
      container.style.width = `${naturalW}px`;
      // After animation, remove explicit width so it stays responsive
      const timer = setTimeout(() => {
        container.style.width = '';
        container.style.transition = '';
      }, DURATION);
      return () => clearTimeout(timer);
    }
  }, [minimized]);

  return (
    <Box
      ref={containerRef}
      {...rest}
      sx={[
        {
          backgroundColor: 'rgba(255, 255, 255, 0.45)',
          border: '1px solid rgba(164, 142, 233, 0.12)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
          borderRadius: 3,
          pointerEvents: 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          cursor: minimized ? 'pointer' : 'grab',
          '&:hover': minimized
            ? { backgroundColor: 'rgba(255, 255, 255, 0.6)' }
            : {},
          '&:active': !minimized ? { cursor: 'grabbing' } : {},
          ...(!minimized && {
            '& button, & a, & input, & textarea, & select, & [role="button"], & .MuiIconButton-root, & .MuiButton-root, & .MuiButtonBase-root, & .MuiListItemButton-root': {
              cursor: 'pointer',
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      onClick={minimized ? onExpand : undefined}
    >
      {/* ── Header — always visible, the shared "hero" element ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: minimized ? 1 : 1.5,
          flexShrink: 0,
          transition: `padding ${DURATION}ms ${EASE}`,
        }}
      >
        <Stack ref={titleGroupRef} direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
          {onBack && !minimized && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onBack(); }}
              sx={{
                color: '#656EFF',
                p: 0.25,
                mr: 0.25,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(101, 110, 255, 0.08)' },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          {icon}
          <Typography
            variant="subtitle1"
            noWrap
            onClick={!minimized && (onBack || onTitleClick) ? (e) => { e.stopPropagation(); onBack ? onBack() : onTitleClick?.(); } : undefined}
            sx={{
              fontWeight: minimized ? 600 : 700,
              fontSize: minimized ? 13 : 15,
              color: !minimized && (onBack || onTitleClick) ? '#656EFF' : '#1a1a2e',
              transition: `font-size ${DURATION}ms ${EASE}, color ${DURATION}ms ${EASE}`,
              ...(!minimized && (onBack || onTitleClick) ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
            }}
          >
            {title}{!minimized && onTitleClick && !onBack ? ' ›' : ''}
          </Typography>
        </Stack>

        {onMinimize && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMinimize();
            }}
            sx={{
              color: '#999',
              opacity: minimized ? 0 : 1,
              transform: minimized ? 'scale(0.5)' : 'scale(1)',
              transition: `opacity ${DURATION * 0.5}ms ${EASE}, transform ${DURATION * 0.5}ms ${EASE}, width ${DURATION * 0.5}ms ${EASE}, padding ${DURATION * 0.5}ms ${EASE}`,
              pointerEvents: minimized ? 'none' : 'auto',
              flexShrink: 0,
              ...(minimized && { width: 0, minWidth: 0, p: 0, overflow: 'hidden' }),
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* ── Body — smoothly reveals / hides via CSS grid ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: minimized ? '0fr' : '1fr',
          maxWidth: minimized ? 0 : 900,
          overflow: 'hidden',
          flexShrink: minimized ? 1 : 0,
          transition: `grid-template-rows ${DURATION}ms ${EASE}, max-width ${DURATION}ms ${EASE}`,
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            minHeight: 0,
            opacity: minimized ? 0 : 1,
            transition: `opacity ${Math.round(DURATION * 0.6)}ms ${EASE} ${minimized ? '0ms' : `${Math.round(DURATION * 0.25)}ms`}`,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
